import { ConventionLocationStyle } from "app-constants/constants"
import { createPath, createURL_SSR, ROUTE } from "app-constants/routing"
import { ContentContainer } from "components/content-container"
import { ExhibitorsBrowseAll } from "components/exhibitors/ExhibitorsBrowseAll"
import { NoExhibitorsFallback } from "components/exhibitors/NoExhibitorsFallback"
import MainLayout from "components/main-layout"
import buildServerSidePage from "lib/build-server-side-page"
import buildServerSideProps from "lib/build-server-side-props"
import { getExhibitorsCopyText } from "lib/exhibitors/getExhibitorsCopyText"
import applyCache from "lib/mw/apply-cache"
import applyConvention from "lib/mw/apply-convention"
import applyCurrentContext from "lib/mw/apply-current-context"
import applyNextSeo from "lib/mw/apply-next-seo"
import applyRedirectForAutomaticallyGeneratedConvention from "lib/mw/apply-redirect-for-automatically-generated-convention"
import applyTitle from "lib/mw/apply-title"
import postActiveAuthenticatedVisitors from "lib/postActiveAuthenticatedVisitors"
import useCurrent from "lib/use-current"
import { Filter } from "models/convention-api/types"
import Convention from "models/convention-api/v1/Convention"
import ConventionProviderExhibitor from "models/convention-api/v1/ConventionProviderExhibitor"
import ConventionProviderExhibitorCount from "models/convention-api/v1/ConventionProviderExhibitorCount"
import { useRouter } from "next/router"
import React, { useEffect, useMemo } from "react"

export const getServerSideProps = buildServerSideProps(
  applyCache(),
  applyCurrentContext(),
  applyConvention(),
  applyRedirectForAutomaticallyGeneratedConvention(),
  applyTitle(({ props }) => getExhibitorsCopyText(props.current).plural),
  [
    async (context) => {
      await ConventionProviderExhibitor.applyOne("exhibitor", {
        filter: {
          disabled: "false",
          ...(context.props.convention.location_style === ConventionLocationStyle.VIRTUAL && {
            exclude_institution_id: context.props.current.config.institution_id,
          }),
        },
      })(context)
    },
  ],
  [
    async (context) => {
      if (!context.props.exhibitor) return

      await ConventionProviderExhibitor.applyMore("exhibitors", {
        limit: 20,
        // Default is ascending (a-z)
        sort: "institution.name",
        filter: {
          disabled: "false",
          convention_id: context.query.convention_id as string,
          ...(context.props.convention.location_style === ConventionLocationStyle.VIRTUAL && {
            exclude_institution_id: context.props.current.config.institution_id,
          }),
        },
        includes: ["institution", "institution.chatAvailable"],
      })(context)
    },
    async (context) => {
      if (!context.props.exhibitor) return

      await ConventionProviderExhibitorCount.applyOne("exhibitorsCount", {
        filter: {
          convention_id: context.query.convention_id as string,
          disabled: "false",
          ...(context.props.convention.location_style === ConventionLocationStyle.VIRTUAL && {
            exclude_institution_id: context.props.current.config.institution_id,
          }),
        },
      })(context)
    },
  ],
  applyNextSeo((context) => ({
    openGraph: {
      title: `${context.props.convention.name} - Exhibitors`,
      url: createURL_SSR(context, {
        path: ROUTE.CONVENTION_EXHIBITORS,
        params: { conventionId: context.props.convention.id },
      }),
      type: "website",
      images: [
        {
          url: context.props.convention.logo_url,
          alt: `${context.props.convention.name} - Exhibitors`,
        },
      ],
    },
  })),
)

interface ConventionExhibitorsPageProps {
  exhibitor: ConventionProviderExhibitor
  exhibitors: ConventionProviderExhibitor[][]
  exhibitorsCount: ConventionProviderExhibitorCount
  convention: Convention
}

export default buildServerSidePage(function ConventionExhibitorsPage(
  props: ConventionExhibitorsPageProps,
) {
  const { exhibitor, exhibitors, exhibitorsCount, convention } = props
  const current = useCurrent()
  const router = useRouter()

  useEffect(() => {
    const postActiveUser = async () => {
      try {
        await postActiveAuthenticatedVisitors({
          current,
          convention_convention_id: router.query.convention_id as string,
        })
      } catch (error) {
        console.error(error)
      }
    }

    // @ts-ignore
    if (current.user && typeof router.query.convention_id === "string") {
      postActiveUser()
    }
  }, [current])

  const exhibitorCardHref = createPath({
    path: ROUTE.CONVENTION_EXHIBITOR,
    params: { conventionId: router.query.convention_id as string, exhibitorId: "TO_REPLACE" },
  }).replace("TO_REPLACE", "")

  const filter: Filter | undefined = useMemo(() => {
    if (convention.location_style === ConventionLocationStyle.VIRTUAL) {
      return {
        exclude_institution_id: current?.config.institution_id,
      }
    }

    return undefined
  }, [convention.location_style, current?.config.institution_id])

  return (
    <MainLayout layout="micro">
      <ContentContainer>
        <NoExhibitorsFallback hasExhibitors={!!exhibitor}>
          <ExhibitorsBrowseAll
            filter={filter}
            exhibitorCardHref={exhibitorCardHref}
            conventionIds={router.query.convention_id as string}
            fallbackDataExhibitors={exhibitors}
            fallbackDataExhibitorsCount={exhibitorsCount}
          />
        </NoExhibitorsFallback>
      </ContentContainer>
    </MainLayout>
  )
})
